import React, { useState } from "react";
import { toast } from "sonner";

function WAIcon({ size = 14 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
            <path d="M19.11 17.21c-.27-.13-1.6-.79-1.85-.88-.25-.09-.43-.13-.6.14-.18.27-.69.88-.85 1.06-.16.18-.31.2-.58.07-.27-.13-1.13-.42-2.16-1.34-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.41.12-.55.13-.13.27-.31.4-.47.13-.16.18-.27.27-.45.09-.18.04-.34-.02-.47-.07-.13-.6-1.45-.82-1.99-.22-.52-.44-.45-.6-.46h-.51c-.18 0-.47.07-.71.34-.25.27-.94.92-.94 2.25 0 1.33.97 2.61 1.1 2.79.13.18 1.91 2.92 4.64 4.09.65.28 1.16.45 1.56.58.65.21 1.25.18 1.72.11.52-.08 1.6-.65 1.83-1.28.23-.63.23-1.18.16-1.29-.07-.11-.25-.18-.52-.31zM16.02 6.4c-5.3 0-9.6 4.3-9.6 9.6 0 1.7.45 3.36 1.3 4.82L6.4 25.6l4.94-1.29a9.6 9.6 0 1 0 4.68-17.91zm0 17.51a7.92 7.92 0 0 1-4.03-1.1l-.29-.17-2.93.77.78-2.86-.19-.3a7.92 7.92 0 1 1 6.66 3.66z"/>
        </svg>
    );
}

// Build a WhatsApp URL that avoids the api.whatsapp.com redirect (blocked in some embed contexts).
function buildUrl(text, phone) {
    const cleanPhone = phone ? String(phone).replace(/\D/g, "") : "";
    const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    const enc = encodeURIComponent(text);
    if (isMobile) {
        // wa.me opens the native app directly on mobile
        return cleanPhone ? `https://wa.me/${cleanPhone}?text=${enc}` : `https://wa.me/?text=${enc}`;
    }
    // Desktop: use web.whatsapp.com/send directly (bypasses api.whatsapp.com redirect)
    return cleanPhone
        ? `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${enc}`
        : `https://web.whatsapp.com/send?text=${enc}`;
}

async function copyText(text) {
    try {
        if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
        const ta = document.createElement("textarea");
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        return true;
    } catch { return false; }
}

export default function ShareWhatsApp({ text, phone, label = "Share to WhatsApp", testid, className = "" }) {
    const [busy, setBusy] = useState(false);
    const url = buildUrl(text, phone);

    const openWA = () => {
        setBusy(true);
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (!win || win.closed || typeof win.closed === "undefined") {
            // Popup blocked (common inside iframes) → fall back to copy
            copyText(text).then((ok) => {
                if (ok) toast.success("Popup blocked. Message copied — paste it into your WhatsApp group.");
                else toast.info("Open WhatsApp and paste the message manually.");
            });
        }
        setBusy(false);
    };

    const copyOnly = async () => {
        const ok = await copyText(text);
        toast[ok ? "success" : "error"](ok ? "Message copied to clipboard" : "Could not copy");
    };

    return (
        <div className={`inline-flex items-center gap-1 ${className}`}>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openWA(); }}
                data-testid={testid || "share-wa-btn"}
                className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md bg-[#25D366] text-white hover:bg-[#1ebe57] transition-colors"
            >
                <WAIcon /> {busy ? "…" : label}
            </a>
            <button
                type="button"
                onClick={copyOnly}
                data-testid={(testid || "share-wa") + "-copy"}
                title="Copy message"
                className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-nest-line hover:border-nest-ink text-nest-ink2 hover:text-nest-ink"
            >
                Copy
            </button>
        </div>
    );
}
